import { useState } from "react";
import { createHousehold, joinHousehold } from "../lib/households";

interface Props {
  onDone: () => void;
}

type Mode = "choose" | "create" | "join";

export default function HomeSetupScreen({ onDone }: Props) {
  const [mode, setMode] = useState<Mode>("choose");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) {
      alert("Name your home — e.g. 'Blr Home'.");
      return;
    }
    setSubmitting(true);
    try {
      await createHousehold(trimmed);
      onDone();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Could not create home");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleJoin() {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      alert("Enter the 6-character invite code.");
      return;
    }
    setSubmitting(true);
    try {
      await joinHousehold(trimmed);
      onDone();
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : "Could not join home");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="app-shell home-setup">
      <h1 className="home-setup__title">Set up a home</h1>
      <p className="home-setup__sub">
        A home is where your plants live. You can be in more than one.
      </p>

      {mode === "choose" && (
        <div className="home-setup__choose">
          <button
            type="button"
            className="primary-btn"
            onClick={() => setMode("create")}
          >
            Create a new home
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => setMode("join")}
          >
            Join with an invite code
          </button>
        </div>
      )}

      {mode === "create" && (
        <div className="home-setup__form">
          <label className="section-label">HOME NAME</label>
          <input
            className="text-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Blr Home"
          />
          <button
            type="button"
            className="primary-btn"
            onClick={handleCreate}
            disabled={submitting}
            style={{ marginTop: 14 }}
          >
            {submitting ? "Creating…" : "Create home"}
          </button>
          <button
            type="button"
            className="link-btn"
            onClick={() => setMode("choose")}
          >
            ← Back
          </button>
        </div>
      )}

      {mode === "join" && (
        <div className="home-setup__form">
          <label className="section-label">INVITE CODE</label>
          <input
            className="text-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABCDEF"
            maxLength={6}
            style={{ letterSpacing: 3, textTransform: "uppercase" }}
          />
          <button
            type="button"
            className="primary-btn"
            onClick={handleJoin}
            disabled={submitting}
            style={{ marginTop: 14 }}
          >
            {submitting ? "Joining…" : "Join home"}
          </button>
          <button
            type="button"
            className="link-btn"
            onClick={() => setMode("choose")}
          >
            ← Back
          </button>
        </div>
      )}

      <style>{`
        .home-setup { padding-top: 32px; }
        .home-setup__title {
          font-family: Georgia, serif;
          font-size: 24px;
          font-weight: 700;
          color: #1f5a39;
          margin: 0 0 4px 0;
        }
        .home-setup__sub {
          font-size: 13px;
          color: #6b7a6e;
          margin: 0 0 24px 0;
        }
        .home-setup__choose {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .home-setup__form {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .link-btn {
          align-self: center;
          margin-top: 14px;
          background: none;
          border: none;
          color: #6b7a6e;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
}
